import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../api/config';
import type { License } from '../types';
import {
  Card,
  CardContent,
  Input,
  Badge,
  PageHeader,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableSkeleton,
  EmptyState,
  Button,
  Alert,
} from '@license-management/ui';
import { Search, Key, AlertCircle } from 'lucide-react';

// Format module name by removing mod_ prefix
const formatModuleName = (moduleId: string): string => {
  return moduleId.replace(/^mod_/i, '');
};

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'expired':
      return 'warning';
    case 'revoked':
    case 'inactive':
      return 'error';
    default:
      return 'default';
  }
};

function LicensesPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['licenses'],
    queryFn: () => apiClient.get<License[]>(API_ENDPOINTS.licenses),
  });

  // Filter licenses based on search query
  const filteredLicenses = useMemo(() => {
    if (!data) return [];
    if (!searchQuery) return data;

    const query = searchQuery.toLowerCase();
    return data.filter((license) => {
      if (license.licenseKey.toLowerCase().includes(query)) return true;
      if (license.productName.toLowerCase().includes(query)) return true;
      if (license.licenseType.toLowerCase().includes(query)) return true;
      if (license.status.toLowerCase().includes(query)) return true;
      if (
        license.modules?.some(
          (m) =>
            m.toLowerCase().includes(query) ||
            formatModuleName(m).toLowerCase().includes(query)
        )
      )
        return true;
      return false;
    });
  }, [data, searchQuery]);

  if (error) {
    return (
      <Alert variant="error" icon={<AlertCircle className="h-5 w-5" />}>
        Failed to load licenses. Please try again later.
      </Alert>
    );
  }

  return (
    <div>
      <PageHeader
        title="Your Licenses"
        description="View and manage your software licenses"
      />

      {/* Search Bar */}
      <div className="mb-6">
        <Input
          placeholder="Search by license key, product, type, status, or module..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftElement={<Search className="h-5 w-5" />}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>License Key</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Modules</TableHead>
                  <TableHead>Activations</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Expires</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableSkeleton rows={5} columns={7} />
              </TableBody>
            </Table>
          ) : filteredLicenses.length === 0 ? (
            <EmptyState
              icon={<Key className="h-12 w-12" />}
              title={data?.length === 0 ? 'No licenses found' : 'No matches'}
              description={
                data?.length === 0
                  ? 'Contact your administrator to get started.'
                  : 'No licenses match your search criteria.'
              }
              action={
                searchQuery &&
                data &&
                data.length > 0 && (
                  <Button variant="secondary" onClick={() => setSearchQuery('')}>
                    Clear search
                  </Button>
                )
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>License Key</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Modules</TableHead>
                  <TableHead>Activations</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Expires</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLicenses.map((license) => (
                  <TableRow key={license.licenseId}>
                    <TableCell>
                      <Link
                        to={`/licenses/${license.licenseId}`}
                        className="font-medium text-primary-600 hover:text-primary-700"
                      >
                        {license.productName}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-gray-500 text-xs">
                      {license.licenseKey}
                    </TableCell>
                    <TableCell className="capitalize text-gray-600">
                      {license.licenseType}
                    </TableCell>
                    <TableCell>
                      {license.modules && license.modules.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {license.modules.map((module) => (
                            <Badge key={module} variant="primary" size="sm">
                              {formatModuleName(module)}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {license.currentActivations} / {license.maxActivations}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(license.status)}>
                        {license.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {license.expiresAt
                        ? new Date(license.expiresAt).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LicensesPage;
